import VisitQuotation from '../VisitQuotation';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { formatDuration, formatTimePanama } from '../utils/agendaDates';
import ExecutionLocationDetails from './ExecutionLocationDetails';

const OFFICE_CATEGORY_LABELS = {
    calls: 'Llamadas a clientes',
    quotes: 'Cotizaciones y propuestas',
    collections: 'Cobros y cartera',
    followUp: 'Seguimiento comercial',
    administration: 'Administración',
    planning: 'Planificación',
    other: 'Otro trabajo de oficina'
};

const getVisitName = (visit) => visit?.activityType === 'office'
    ? OFFICE_CATEGORY_LABELS[visit?.officeCategory] || 'Tiempo de oficina'
    : visit?.visitType === 'areaProspecting'
        ? visit?.areaName
    : visit?.visitType === 'prospect'
        ? visit?.prospectName
        : visit?.clientName;

const EXECUTION_LABELS = {
    notStarted: { label: 'Planificada', severity: 'secondary', icon: 'pi pi-calendar' },
    inProgress: { label: 'En progreso', severity: 'info', icon: 'pi pi-play-circle' },
    pendingReport: { label: 'Pendiente de reporte', severity: 'warning' },
    completed: { label: 'Completada', severity: 'success', icon: 'pi pi-check-circle' },
    notCompleted: { label: 'No completada', severity: 'danger', icon: 'pi pi-times-circle' }
};

const OUTCOME_LABELS = {
    noChange: 'Sin cambios',
    followUpRequired: 'Requiere seguimiento',
    quoteRequested: 'Solicitó cotización',
    sampleRequested: 'Solicitó muestra',
    opportunityIdentified: 'Oportunidad identificada',
    potentialOrder: 'Pedido potencial',
    complaint: 'Queja',
    notInterested: 'No interesado',
    visitNotCompleted: 'Visita no completada'
};

const getElapsedSeconds = (startedAt) => {
    if (!startedAt) return 0;
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return 0;
    return Math.max(0, Math.floor((Date.now() - start) / 1000));
};

const VisitCard = ({
    visit,
    compact = false,
    canEdit = false,
    canStart = false,
    canFinish = false,
    canReport = false,
    canViewReport = false,
    reportLocked = false,
    actionLoading = false,
    locationLoading = false,
    onEdit,
    onCancel,
    onStart,
    onFinish,
    onReport
}) => {
    const isCancelled = visit?.planningStatus === 'cancelled';
    const isOffice = visit?.activityType === 'office';
    const typeLabel = isOffice
        ? 'Tiempo de oficina'
        : visit?.visitType === 'areaProspecting'
            ? 'Prospección de zona'
            : visit?.visitType === 'prospect'
                ? 'Prospecto'
                : 'Cliente existente';
    const originLabel = visit?.visitOrigin === 'unplanned' ? (isOffice ? 'Iniciado ahora' : 'No planificada') : 'Planificada';
    const execution = EXECUTION_LABELS[visit?.executionStatus || 'notStarted'] || EXECUTION_LABELS.notStarted;
    const agentName = visit?.agent?.name || [visit?.agent?.firstName, visit?.agent?.lastName].filter(Boolean).join(' ') || visit?.agent?.email || null;
    const reportStatus = visit?.reportStatus || (visit?.executionStatus === 'completed' ? 'submitted' : 'notStarted');
    const report = visit?.report || {};
    const reportPhotoCount = Number(report?.photoCount || (Array.isArray(report?.photos) ? report.photos.length : 0));
    const showReportButton = !isOffice && (canReport || canViewReport);
    const reportButtonLabel = reportLocked || !canReport
        ? 'Ver reporte'
        : visit?.executionStatus === 'inProgress'
            ? 'Abrir reunión'
        : reportStatus === 'submitted'
            ? 'Editar reporte'
            : reportStatus === 'draft'
                ? 'Continuar reporte'
                : 'Completar reporte';
    const [elapsedSeconds, setElapsedSeconds] = useState(() => getElapsedSeconds(visit?.startedAt));

    useEffect(() => {
        if (visit?.executionStatus !== 'inProgress') return undefined;
        setElapsedSeconds(getElapsedSeconds(visit?.startedAt));
        const interval = window.setInterval(() => setElapsedSeconds(getElapsedSeconds(visit?.startedAt)), 1000);
        return () => window.clearInterval(interval);
    }, [visit?.executionStatus, visit?.startedAt]);

    const durationLabel = useMemo(() => {
        if (visit?.executionStatus === 'inProgress') return formatDuration(elapsedSeconds);
        if (visit?.durationSeconds !== null && visit?.durationSeconds !== undefined) return formatDuration(visit.durationSeconds);
        return null;
    }, [elapsedSeconds, visit?.durationSeconds, visit?.executionStatus]);

    return (
        <div className={`ventas-visit-card${isOffice ? ' ventas-visit-card--office' : ''}${isCancelled ? ' ventas-visit-card--cancelled' : ''}${visit?.executionStatus === 'inProgress' ? ' ventas-visit-card--active' : ''}${compact ? ' ventas-visit-card--compact' : ''}`}>
            <div className="flex align-items-start justify-content-between gap-2">
                <div className="min-w-0">
                    <div className="ventas-visit-title">{getVisitName(visit) || 'Sin nombre'}</div>
                    <div className="flex gap-2 flex-wrap mt-2">
                        <Tag value={typeLabel} severity={isOffice ? 'secondary' : 'info'} />
                        <Tag value={originLabel} severity={visit?.visitOrigin === 'unplanned' ? 'warning' : 'success'} />
                        <Tag icon={isCancelled ? 'pi pi-ban' : execution.icon} value={isCancelled ? 'Cancelada' : execution.label} severity={isCancelled ? 'warning' : execution.severity} />
                        {!isOffice && reportStatus !== 'notStarted' ? <Tag value={reportStatus === 'submitted' ? 'Reporte enviado' : 'Reporte en borrador'} severity={reportStatus === 'submitted' ? 'success' : 'warning'} /> : null}
                        {!isOffice && reportLocked ? <Tag value="Semana cerrada" severity="secondary" /> : null}
                    </div>
                </div>
                {!compact && !isCancelled && canEdit ? (
                    <div className="flex gap-2 ventas-visit-actions ventas-visit-actions--icon">
                        <Button type="button" icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={() => onEdit(visit)} aria-label="Editar visita" />
                        <Button type="button" icon="pi pi-ban" className="p-button-text p-button-sm p-button-warning" onClick={() => onCancel(visit)} aria-label="Cancelar visita" />
                    </div>
                ) : null}
            </div>

            <div className="ventas-visit-schedule mt-3">
                <span><i className="pi pi-clock" aria-hidden="true" /> {visit?.plannedStartTime && visit?.plannedEndTime ? `${visit.plannedStartTime}–${visit.plannedEndTime}` : visit?.visitOrigin === 'unplanned' ? 'Inicio no planificado' : 'Horario por confirmar'}</span>
                {visit?.estimatedDurationMinutes ? <span><i className="pi pi-hourglass" aria-hidden="true" /> {visit.estimatedDurationMinutes} min estimados</span> : null}
            </div>
            {isOffice ? (
                visit?.officeNotes ? <p className="ventas-visit-objective"><strong>Nota:</strong> {visit.officeNotes}</p> : null
            ) : (
                <p className="ventas-visit-objective"><strong>{visit?.visitType === 'areaProspecting' ? 'Objetivo del recorrido:' : 'Objetivo:'}</strong> {visit?.objective}</p>
            )}
            {agentName ? <div className="text-sm text-color-secondary"><i className="pi pi-user mr-1" aria-hidden="true" />Vendedor: {agentName}</div> : null}
            {!isOffice && !compact && visit?.contactName ? <div className="text-sm text-color-secondary">Contacto: {visit.contactName}</div> : null}
            {!isOffice && !compact && visit?.contactPhone ? <div className="text-sm text-color-secondary">Teléfono: {visit.contactPhone}</div> : null}
            {!isOffice && !compact && visit?.contactEmail ? <div className="text-sm text-color-secondary">Correo: {visit.contactEmail}</div> : null}
            {!isOffice && (visit?.address ? <div className="text-sm text-color-secondary"><i className="pi pi-map-marker mr-1" aria-hidden="true" />Dirección: {visit.address}</div> : <div className="text-sm text-color-secondary"><i className="pi pi-map-marker mr-1" aria-hidden="true" />Dirección no registrada</div>)}
            {visit?.startedAt ? <div className="text-sm text-color-secondary">Inicio: {formatTimePanama(visit.startedAt)}</div> : null}
            {!isOffice && visit?.startedAt ? <ExecutionLocationDetails label="Ubicación de inicio" location={visit?.startLocation} /> : null}
            {visit?.endedAt ? <div className="text-sm text-color-secondary">Finalización: {formatTimePanama(visit.endedAt)}</div> : null}
            {!isOffice && visit?.endedAt ? <ExecutionLocationDetails label="Ubicación de finalización" location={visit?.endLocation} /> : null}
            {durationLabel ? <div className="text-sm text-color-secondary">Duración: {durationLabel}</div> : null}
            {report?.outcome ? <div className="text-sm text-color-secondary">Resultado: {OUTCOME_LABELS[report.outcome] || report.outcome}</div> : null}
            {!compact && report?.summary ? <div className="text-sm text-color-secondary">Resumen: {report.summary}</div> : null}
            {!compact && report?.prospectsDiscovered ? <div className="text-sm text-color-secondary">Prospectos encontrados: {report.prospectsDiscovered}</div> : null}
            {!compact && report?.agreements ? <div className="text-sm text-color-secondary">Acuerdos: {report.agreements}</div> : null}
            {!compact && report?.nextSteps ? <div className="text-sm text-color-secondary">Próximos pasos: {report.nextSteps}</div> : null}
            {!compact && report?.followUpDate ? <div className="text-sm text-color-secondary">Seguimiento: {report.followUpDate}</div> : null}
            {!compact && reportPhotoCount ? <div className="text-sm text-color-secondary">Imágenes: {reportPhotoCount}</div> : null}
            {isCancelled && visit?.cancellationReason ? <div className="text-sm text-color-secondary mt-2">Motivo: {visit.cancellationReason}</div> : null}

            {(canStart || canFinish || showReportButton) && !isCancelled ? (
                <div className="flex gap-2 mt-3 ventas-visit-actions ventas-visit-actions--primary">
                    {canStart ? <Button type="button" label={isOffice ? 'Iniciar oficina' : locationLoading ? 'Obteniendo ubicación...' : 'Empezar visita'} icon="pi pi-play" className="p-button-sm" onClick={() => onStart(visit)} loading={actionLoading} disabled={actionLoading} /> : null}
                    {canFinish ? <Button type="button" label={isOffice ? 'Finalizar oficina' : 'Finalizar visita'} icon="pi pi-stop" className="p-button-sm p-button-warning" onClick={() => onFinish(visit)} loading={actionLoading} disabled={actionLoading} /> : null}
                    {!isOffice && !isCancelled && <VisitQuotation visit={visit} />}
                    {showReportButton ? <Button type="button" label={reportButtonLabel} icon="pi pi-file-edit" className="p-button-sm p-button-outlined" onClick={() => onReport(visit)} loading={actionLoading} disabled={actionLoading} /> : null}
                </div>
            ) : null}

            {compact && !isCancelled && canEdit ? (
                <div className="flex gap-2 mt-3 ventas-visit-actions ventas-visit-actions--planning">
                    <Button type="button" label="Editar" icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => onEdit(visit)} />
                    <Button type="button" label="Cancelar" icon="pi pi-ban" className="p-button-sm p-button-text p-button-warning" onClick={() => onCancel(visit)} />
                </div>
            ) : null}
        </div>
    );
};

export default VisitCard;
